import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Volume2, VolumeX } from 'lucide-react'
import { useNotification } from 'hooks/useNotification'
import { setNotificationSoundEnabled, useNotificationSoundEnabled } from 'utils/notificationSoundUtils'
import { Badge } from 'components/ds2/Badge'
import { Bell } from './icons'
import { useSidebarAnchoredPanel } from './useSidebarAnchoredPanel'

const PANEL_WIDTH = 340

/**
 * Sidebar entry point for the notification list that used to live in a
 * floating, draggable widget (`components/NotificationCenter.tsx`) shown only
 * on a handful of routes. That widget's urgent "special" toasts (duplicate-tab
 * warning, temp call-center pings) still float as before — this button only
 * takes over the idle "browse my notifications" bell + dropdown, so it's a
 * regular sidebar row rather than a corner overlay, and it's visible on every
 * page instead of being route-gated.
 *
 * Reuses `useNotification` directly (Redux-backed) so the count/list here
 * stay in sync with the floating widget's special-alert state automatically —
 * no prop drilling or duplicated store access needed.
 */
export const SidebarNotificationButton = ({ isOpen }: { isOpen: boolean }) => {
    const navigate = useNavigate()
    const { notifications, clearNotification, clearAll } = useNotification()
    const isSoundEnabled = useNotificationSoundEnabled()
    const [panelOpen, setPanelOpen] = useState(false)
    const panelRef = useRef<HTMLDivElement>(null)

    // Same topics the floating widget treats as "special" (auto-popup toasts,
    // not list items) — excluded here too so they aren't shown twice.
    const displayedNotifications = notifications
        .filter((n) => !['temp', 'duplicate_connection', 'permanent'].includes(n.topic ?? ''))
        .sort((a, b) => b.timestamp - a.timestamp)

    // This row sits low in the sidebar (footer, just above the profile row), so opening
    // downward like a typical dropdown leaves little to no room and collides with the
    // profile row underneath — same reasoning as `ProfileFooterItem`'s popover. `above`
    // bottom-anchors the panel so it opens upward, pinned to the trigger regardless of its
    // own content height; `right` (collapsed rail) anchors it beside the icon instead.
    const { coords, triggerRef, computeCoords } = useSidebarAnchoredPanel<HTMLButtonElement>(
        isOpen ? 'above' : 'right',
        panelOpen,
        isOpen ? PANEL_WIDTH : undefined
    )

    useEffect(() => {
        if (!panelOpen) return
        const handleOutside = (e: MouseEvent) => {
            if (triggerRef.current && !triggerRef.current.contains(e.target as Node) && panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setPanelOpen(false)
            }
        }
        document.addEventListener('mousedown', handleOutside)
        return () => document.removeEventListener('mousedown', handleOutside)
    }, [panelOpen, triggerRef])

    const handleTrigger = () => {
        if (!panelOpen) computeCoords()
        setPanelOpen((p) => !p)
    }

    const formatTimeAgo = (timestamp: number): string => {
        const seconds = Math.floor((Date.now() - timestamp) / 1000)
        if (seconds < 60) return 'just now'
        const minutes = Math.floor(seconds / 60)
        if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
        const hours = Math.floor(minutes / 60)
        if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
        const days = Math.floor(hours / 24)
        return days < 7 ? `${days} ${days === 1 ? 'day' : 'days'} ago` : new Date(timestamp).toLocaleDateString()
    }

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onClick={handleTrigger}
                title="Notifications"
                className={[
                    'relative flex h-9 items-center justify-center rounded-xl transition-colors',
                    isOpen ? 'flex-1' : 'w-full',
                    panelOpen ? 'bg-accent-soft text-foreground' : 'text-foreground hover:bg-default',
                ].join(' ')}
            >
                <span className="relative inline-flex">
                    <Bell size={18} />
                    {displayedNotifications.length > 0 && (
                        <span className="absolute -right-2.5 -top-3.5 z-10">
                            <Badge
                                variant="primary"
                                type="accent"
                                size="sm"
                                label={String(displayedNotifications.length)}
                                style={{ border: 'none', minWidth: 14, height: 14, padding: '0 2px', fontSize: 9, justifyContent: 'center' }}
                            />
                        </span>
                    )}
                </span>
            </button>

            {panelOpen &&
                coords &&
                createPortal(
                    <div
                        ref={panelRef}
                        style={{ position: 'fixed', bottom: coords.bottom, left: coords.left, width: PANEL_WIDTH, zIndex: 50 }}
                        className="flex flex-col rounded-2xl border border-border bg-surface shadow-overlay"
                    >
                        <div className="flex items-center justify-between border-b border-border p-3">
                            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                <Bell size={16} />
                                Notifications
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => setNotificationSoundEnabled(!isSoundEnabled)}
                                    title={isSoundEnabled ? 'Turn off notification sounds' : 'Turn on notification sounds'}
                                    aria-label={isSoundEnabled ? 'Turn off notification sounds' : 'Turn on notification sounds'}
                                    className="rounded-lg p-1.5 text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
                                >
                                    {isSoundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                                </button>
                                {displayedNotifications.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => clearAll()}
                                        className="rounded-lg px-2 py-1 text-xs font-medium text-foreground-muted transition-colors hover:bg-default"
                                    >
                                        Clear all
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex max-h-[440px] flex-col gap-1 overflow-y-auto p-2">
                            {displayedNotifications.length === 0 ? (
                                <div className="flex flex-col items-center gap-1 py-6 text-center">
                                    <Bell size={22} className="text-foreground-muted" />
                                    <span className="text-sm text-foreground-muted">No new notifications</span>
                                </div>
                            ) : (
                                displayedNotifications.map((notification, index) => (
                                    <button
                                        key={`${notification.timestamp}-${index}`}
                                        type="button"
                                        onClick={() => {
                                            clearNotification(notification)
                                            setPanelOpen(false)
                                            if (notification.onClick) notification.onClick()
                                            else if (notification.path) navigate(notification.path)
                                        }}
                                        className={[
                                            'flex flex-col gap-1 rounded-xl p-2.5 text-left transition-colors hover:bg-default',
                                            notification.type === 'error'
                                                ? 'border-l-2 border-danger'
                                                : notification.type === 'warning'
                                                  ? 'border-l-2 border-warning'
                                                  : 'border-l-2 border-accent',
                                        ].join(' ')}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-semibold text-foreground">{notification.message}</span>
                                            <span className="shrink-0 text-[11px] text-foreground-muted">{formatTimeAgo(notification.timestamp)}</span>
                                        </div>
                                        {notification.secondaryMessage && (
                                            <span className="text-xs text-foreground-muted">{notification.secondaryMessage}</span>
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>,
                    document.body
                )}
        </>
    )
}
